import { useState, useEffect } from 'react';
import { get, post } from '../../../api/client';
import { useAuthStore } from '../../../stores/authStore';
import type { CustomerCandidate } from '../../../api/customer';
import { verifyCustomer, linkCustomer } from '../../../api/customer';
import CustomerMergeCandidates from './CustomerMergeCandidates';

interface CustomerType {
  id: string;
  name: string;
}

interface AvailableTag {
  id: string;
  name: string;
  color: string | null;
}

interface FormState {
  fullName: string;
  phone: string;
  backupPhone: string;
  companyName: string;
  location: string;
  address: string;
  emergencyContact: string;
  emergencyPhone: string;
  introduction: string;
  customerTypeId: string;
  tagId: string;
}

const INITIAL_FORM: FormState = {
  fullName: '',
  phone: '',
  backupPhone: '',
  companyName: '',
  location: '',
  address: '',
  emergencyContact: '',
  emergencyPhone: '',
  introduction: '',
  customerTypeId: '',
  tagId: '',
};

interface CustomerFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'form' | 'review';

export default function CustomerFormModal({ open, onClose, onSuccess }: CustomerFormModalProps) {
  const { profile } = useAuthStore();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [customerTypes, setCustomerTypes] = useState<CustomerType[]>([]);
  const [availableTags, setAvailableTags] = useState<AvailableTag[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 审核面板状态
  const [step, setStep] = useState<Step>('form');
  const [candidates, setCandidates] = useState<CustomerCandidate[]>([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [acquaintanceConfirmed, setAcquaintanceConfirmed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(INITIAL_FORM);
    setError(null);
    setStep('form');
    setCandidates([]);
    setSelectedCandidateId(null);
    setAcquaintanceConfirmed(false);

    const fetchOptions = async () => {
      try {
        const [types, tags] = await Promise.all([
          get<CustomerType[]>('/customer-types'),
          get<AvailableTag[]>('/customer-tags'),
        ]);
        setCustomerTypes(types ?? []);
        setAvailableTags(tags ?? []);
      } catch (error) {
        console.error('获取客户类型和标签失败:', error);
        setCustomerTypes([]);
        setAvailableTags([]);
      }
    };
    fetchOptions();
  }, [open]);

  const setTag = (tagId: string) => {
    setForm((prev) => ({
      ...prev,
      tagId: prev.tagId === tagId ? '' : tagId,
    }));
  };

  // 正文里没有 email / idCard 两个键：身份证号与邮箱是本人注册账号时才填的登录凭据，
  // 不该由添加客户的人代填；后端按「键缺失＝本次不改」处理，不会抹掉既有值。
  const buildCustomerPayload = () => ({
    fullName: form.fullName.trim(),
    phone: form.phone.replace(/\s+/g, '').trim() || null,
    backupPhone: form.backupPhone.replace(/\s+/g, '').trim() || null,
    companyName: form.companyName.trim() || null,
    location: form.location.trim() || null,
    address: form.address.trim() || null,
    emergencyContact: form.emergencyContact.trim() || null,
    emergencyPhone: form.emergencyPhone.trim() || null,
    introduction: form.introduction.trim() || null,
    customerTypeId: form.customerTypeId || null,
    tagId: form.tagId || null,
  });

  const createFresh = async () => {
    const resp = await post<{ customerId?: string; success?: boolean; message?: string }>(
      '/customers',
      buildCustomerPayload()
    );
    if (resp && (resp as { success?: boolean }).success === false) {
      throw new Error((resp as { message?: string }).message || '创建失败');
    }
  };

  // 将 verify 返回的候选列表切换到 review 面板
  const enterReviewWithCandidates = (cands: CustomerCandidate[]) => {
    setCandidates(cands);
    setSelectedCandidateId(cands.length === 1 ? cands[0].id : null);
    setAcquaintanceConfirmed(false);
    setStep('review');
  };

  const handleSubmit = async () => {
    if (!form.fullName.trim()) {
      setError('客户姓名为必填项');
      return;
    }
    const phoneClean = form.phone.replace(/\s+/g, '').trim();
    if (!phoneClean) {
      setError('手机号码为必填项');
      return;
    }
    if (!/^1\d{10}$/.test(phoneClean)) {
      setError('请输入正确的手机号码（11位数字，以1开头）');
      return;
    }
    if (!profile?.id) return;
    setSubmitting(true);
    setError(null);

    try {
      // 先做 verify：client.post 已解包，返回体直接就是 { kind, candidates, ... }
      const verifyResp = await verifyCustomer(buildCustomerPayload());
      const kind = verifyResp?.kind ?? 'none';
      const cands = (verifyResp?.candidates ?? []) as CustomerCandidate[];

      if (kind === 'none') {
        // 无重复：直接创建
        await createFresh();
        onSuccess();
        onClose();
        return;
      }

      // 命中：切到审核面板（显示完整系统候选人员信息 + 熟人核对 + 绑定确认）
      enterReviewWithCandidates(cands);
    } catch (err: unknown) {
      // 兜底：后端 POST /customers 在姓名不一致时会返回
      // `code: NAME_MISMATCH_REVIEW_REQUIRED`，此时必须回到 review 面板让用户核对绑定，
      // 不能只做 toast / 红字提示，否则用户无法完成绑定（身份唯一性宪法·规则 5）。
      const e = err as { code?: string; message?: string } | null;
      if (e && e.code === 'NAME_MISMATCH_REVIEW_REQUIRED') {
        try {
          // 重新 verify 获取完整候选结构（含 hitFields / orderCount / idCard 等），
          // 比仅依赖后端返回的精简 candidate 体验更完整
          const reVerify = await verifyCustomer(buildCustomerPayload());
          const cands = (reVerify?.candidates ?? []) as CustomerCandidate[];
          if (cands.length > 0) {
            enterReviewWithCandidates(cands);
            return;
          }
        } catch {
          // 忽略二次 verify 错误，回落到通用提示
        }
      }
      setError(err instanceof Error ? err.message : '创建失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmReuse = async () => {
    if (!selectedCandidateId) {
      setError('请选择要复用的客户');
      return;
    }
    if (!acquaintanceConfirmed) {
      setError('请先完成熟人核对并勾选确认');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // 复用已绑定客户时，未被用户选中的关系字段整键省略：
      // buildCustomerPayload 恒发 customerTypeId/tagId=null，后端把 null 视作「显式清空」，
      // 会抹掉该用户名下这条绑定既有的客户类型与标签。省略后到达后端为 undefined→保留原值。
      const payload = buildCustomerPayload() as Record<string, unknown>;
      if (!form.customerTypeId) delete payload.customerTypeId;
      if (!form.tagId) delete payload.tagId;
      await linkCustomer(selectedCandidateId, payload);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '绑定失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToForm = () => {
    setStep('form');
    setError(null);
  };

  if (!open) return null;

  const inputCls =
    'w-full px-3.5 py-3 sm:py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 bg-white text-gray-800 placeholder:text-gray-400 transition-colors';
  const labelCls = 'block text-xs font-medium text-gray-500 mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Sheet / Dialog */}
      <div className="relative bg-white w-full sm:max-w-lg sm:mx-4 rounded-t-2xl sm:rounded-2xl max-h-[92vh] sm:max-h-[90vh] overflow-hidden flex flex-col">

        {/* Mobile drag handle */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 sm:px-6 sm:py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {step === 'form' ? '新建客户' : '发现可能重复的客户'}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {step === 'form' ? '填写客户基本信息' : '请核对后选择复用主档，避免重复创建'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 sm:py-5 space-y-4">
          {step === 'form' && (
            <>
              {/* 姓名 */}
              <div>
                <label className={labelCls}>
                  客户姓名 <span className="text-red-400">*</span>
                </label>
                <input
                  value={form.fullName}
                  onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))}
                  placeholder="请输入客户姓名"
                  className={inputCls}
                />
              </div>

              {/* 手机 + 备用 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>
                    手机号码 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                    placeholder="请输入11位手机号"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>备用号码</label>
                  <input
                    type="tel"
                    value={form.backupPhone}
                    onChange={(e) => setForm((p) => ({ ...p, backupPhone: e.target.value }))}
                    placeholder="备用联系方式"
                    className={inputCls}
                  />
                </div>
              </div>

              {/* 公司 */}
              <div>
                <label className={labelCls}>公司/单位</label>
                <input
                  value={form.companyName}
                  onChange={(e) => setForm((p) => ({ ...p, companyName: e.target.value }))}
                  placeholder="所在公司或单位名称"
                  className={inputCls}
                />
              </div>

              {/* 城市 + 类型 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>所在城市</label>
                  <input
                    value={form.location}
                    onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
                    placeholder="如：北京、上海"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>客户类型</label>
                  <select
                    value={form.customerTypeId}
                    onChange={(e) => setForm((p) => ({ ...p, customerTypeId: e.target.value }))}
                    className={`${inputCls} cursor-pointer`}
                  >
                    <option value="">请选择类型</option>
                    {customerTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 标签 */}
              {availableTags.length > 0 && (
                <div>
                  <label className={labelCls}>客户标签</label>
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map((tag) => {
                      const selected = form.tagId === tag.id;
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => setTag(tag.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer"
                          style={
                            selected
                              ? {
                                  backgroundColor: `${tag.color ?? '#14b8a6'}25`,
                                  color: tag.color ?? '#14b8a6',
                                  border: `1.5px solid ${tag.color ?? '#14b8a6'}`,
                                }
                              : { backgroundColor: '#f9fafb', color: '#9ca3af', border: '1.5px solid #e5e7eb' }
                          }
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full shrink-0"
                            style={{ backgroundColor: selected ? (tag.color ?? '#14b8a6') : '#d1d5db' }}
                          />
                          {tag.name}
                          {selected && (
                            <div className="w-3 h-3 flex items-center justify-center shrink-0">
                              <i className="ri-check-line text-xs" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 备注 */}
              <div>
                <label className={labelCls}>备注信息</label>
                <textarea
                  value={form.introduction}
                  onChange={(e) => setForm((p) => ({ ...p, introduction: e.target.value }))}
                  placeholder="关于这位客户的备注..."
                  rows={3}
                  maxLength={500}
                  className={`${inputCls} resize-none`}
                />
                <p className="text-right text-xs text-gray-300 mt-1">{form.introduction.length}/500</p>
              </div>
            </>
          )}

          {step === 'review' && (
            <>
              <CustomerMergeCandidates
                candidates={candidates}
                selectedId={selectedCandidateId}
                onSelect={setSelectedCandidateId}
              />

              {/* 熟人核对确认 */}
              <label className="flex items-start gap-2 p-3 rounded-xl bg-teal-50/60 border border-teal-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acquaintanceConfirmed}
                  onChange={(e) => setAcquaintanceConfirmed(e.target.checked)}
                  className="mt-0.5 accent-teal-500 cursor-pointer"
                />
                <span className="text-xs text-teal-800 leading-relaxed">
                  我已通过微信 / 电话等真实渠道<span className="font-semibold">联系客户本人</span>，
                  确认所选客户身份与当前信息为同一人，同意将新信息绑定到该主档。
                </span>
              </label>
            </>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-500">
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-error-warning-line" />
              </div>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-4 border-t border-gray-100 flex items-center gap-3">
          {step === 'form' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 sm:flex-none sm:px-5 py-2.5 sm:py-2 text-sm text-gray-500 border border-gray-200 bg-white rounded-xl cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 sm:flex-none sm:px-6 py-2.5 sm:py-2 text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 disabled:opacity-60 rounded-xl cursor-pointer transition-colors whitespace-nowrap"
              >
                {submitting ? '校验中...' : '创建客户'}
              </button>
            </>
          )}
          {step === 'review' && (
            <>
              <button
                type="button"
                onClick={handleBackToForm}
                className="flex-1 sm:flex-none sm:px-5 py-2.5 sm:py-2 text-sm text-gray-500 border border-gray-200 bg-white rounded-xl cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                返回修改
              </button>
              <button
                type="button"
                onClick={handleConfirmReuse}
                disabled={submitting || !selectedCandidateId || !acquaintanceConfirmed}
                className="flex-1 sm:flex-none sm:px-6 py-2.5 sm:py-2 text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 disabled:opacity-60 rounded-xl cursor-pointer transition-colors whitespace-nowrap"
              >
                {submitting ? '绑定中...' : '复用并绑定到我'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
